<script lang="ts" setup>
import { FileText, Link as LucideLink, Mail, Phone, Send, Shield, Users } from 'lucide-vue-next'

// Props для управления состоянием из родительского компонента
const props = defineProps<{
  isOpen?: boolean
}>()
// Emits для уведомления родителя об изменении состояния
const emit = defineEmits<{
  'update:isOpen': [value: boolean]
}>()
const documentStore = useDocumentStore()
function openPolicy() {
  documentStore.openDocument('pk_site.pdf', 'Политика конфиденциальности')
}
const mainStore = useMainStore()

// Локальное состояние для обратной совместимости (если props не переданы)
const localIsOpen = ref(false)

// Используем props.isOpen если передан, иначе локальное состояние
const isOpen = computed({
  get: () => props.isOpen !== undefined ? props.isOpen : localIsOpen.value,
  set: (value: boolean) => {
    if (props.isOpen !== undefined) {
      emit('update:isOpen', value)
    }
    else {
      localIsOpen.value = value
    }
  },
})

const { handleItemClick } = useHandleMenuClick(isOpen)
const menuItems = [
  { title: 'Мы', href: '/manifest/' },
  { title: 'О сервисе', href: '#whyclients' },
  { title: 'Как получить', href: '#zaemsteps' },
  { title: 'Как оплатить', href: '#howpay' },
  { title: 'Вопросы и ответы', href: '#faq' },
  { title: 'Документы', href: '/documents/' },
  {
    title: 'Политика конфиденциальности',
    href: '',
    action: openPolicy,
  },
]
</script>

<template>
  <footer class="bg-gray-800 text-gray-300 py-10 border-t border-gray-700">
    <div class="container mx-auto px-4">
      <!-- Логотип -->
      <div class="mb-6 flex justify-center md:justify-start">
        <NuxtImg
          src="/logo_footer.svg"
          alt="ООО МКК «Небус»"
          class="w-28 h-10"
        />
      </div>
      <!-- Основные блоки -->
      <div class="flex flex-col md:flex-row gap-8 md:gap-0 mb-10">
        <!-- Навигация -->
        <nav class="flex-1 flex flex-col gap-3 md:gap-2 items-center md:items-start md:pr-8">
          <a
            v-for="(item, index) in menuItems"
            :key="index"
            :href="item.href ?? '#'"
            class="hover:text-primary transition-colors flex items-center gap-2 group text-center md:text-left"
            @click.prevent="item.action ? item.action() : handleItemClick($event, item.href)"
          >
            <span class="text-sm md:text-base">{{ item.title }}</span>
          </a>
        </nav>
        <!-- Контакты -->
        <div class="flex-1 flex flex-col gap-3 items-center md:px-8 border-gray-600">
          <div class="flex items-center gap-2">
            <span class="text-gray-400">Центр приема обращений</span>
          </div>
          <a href="tel:88007707345" class="text-white hover:underline">
            8 (800) 770-73-45
          </a>
          <div class="flex items-center gap-2">
            <span class="text-gray-400">Жалобы и обращения</span>
          </div>
          <a href="mailto:complaints@nebusfinance.ru" class="text-white hover:underline">
            complaints@nebusfinance.ru
          </a>
          <div class="mt-2 text-center">
            <span class="text-gray-400">Обработка заявок на заем</span>
            <p class="text-white">
              круглосуточно без выходных
            </p>
          </div>
        </div>
        <!-- Обратная связь -->
        <div class="flex-1 flex flex-col items-center md:pl-8">
          <Button
            class="border-none text-white relative group w-56 mb-3 bg-primary hover:bg-primary/90 shadow-lg transition-all"
            @click="mainStore.feedbackOpen = true"
          >
            <div class="px-4 py-2 rounded-lg font-medium">
              Поделиться мнением
            </div>
          </Button>
          <NuxtLink to="/survey">
            <Button class="border-none text-white relative group w-56 bg-gray-700 hover:bg-primary/80 shadow transition-all">
              <div class="px-4 py-2 rounded-lg font-medium">
                Пройти опрос
              </div>
            </Button>
          </NuxtLink>
          <div class="flex items-center justify-center gap-2 mt-4 w-56">
            <NuxtLink to="https://t.me/nebus8" aria-label="Telegram канал" class="flex items-center gap-2 text-gray-400 hover:text-primary transition-colors">
              <Send class="w-5 h-5 text-primary" />
              <span>Telegram</span>
            </NuxtLink>
          </div>
          <div class="mt-4 md:mt-auto flex justify-center w-56 rounded-lg">
            <YandexRatingWidget />
          </div>
        </div>
      </div>
      <!-- Юридическая информация -->
      <div class="pt-6 space-y-4 text-xs text-gray-400">
        <div class="grid grid-cols-1 gap-6">
          <div class="space-y-2">
            <p>ООО МКК "Небус" исключено из государственного реестра МФО на основании собственного заявления. Для клиентов ООО МКК "Небус" порядок погашения задолженности не меняется, Вы по-прежнему можете погасить заем в личном кабинете и по реквизитам через банк.</p>
            <p>Выдачу займов под брендом Nebus осуществляет ООО МКК "Луна"</p>
          </div>
          <div class="space-y-2">
            <p>ООО МКК «Луна» состоит в государственном <a href="https://cbr.ru/finorg/foinfo/?ogrn=1237700707078" target="_blank" class="text-primary hover:underline">реестре микрофинансовых организаций Банка России</a>. Регистрационный номер записи № 24–030-45-010028 от 04.03.2024 г.</p>
            <p>Общество с ограниченной ответственностью Микрокредитная компания «Луна» (ООО МКК «Луна»). ОГРН: 1237700707078 ИНН: 9728109264</p>
            <LinksBlock />
            <p>Является действующим членом СРО Союза «МИКРОФИНАНСОВЫЙ АЛЬЯНС» «Институты развития малого и среднего бизнеса» от 19.06.2025г. за № 0625030772249. Адрес саморегулируемой организации: 125367, г. Москва, Полесский пр-д, д. 16, стр. 1, пом. 308 эт. Антресоль. <a href="https://alliance-mfo.ru/" target="_blank" class="text-primary hover:underline">https://alliance-mfo.ru/</a>, e-mail: <a href="mailto:info@alliance-mfo.ru" class="text-primary hover:underline">info@alliance-mfo.ru</a></p>
            <p class="font-medium text-white">
              ПСК от 0,000% до 292,000%
            </p>
          </div>
        </div>
        <!-- Предупреждение о cookies -->
        <div class="pt-4 border-t border-gray-600 mt-2">
          <p>
            ООО МКК «Небус» и ООО МКК «Луна»
            <a href="/privacy" class="text-primary hover:underline">использует файлы «cookie»</a>, с целью персонализации сервисов и повышения удобства пользования веб-сайтом. Если вы не хотите использовать файлы «cookie», измените настройки своего браузера.
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
@media (max-width: 768px) {
  .md\:divide-x > :not([hidden]) ~ :not([hidden]) {
    border-left: none !important;
  }
}
</style>
